import { format, schAloftArea } from '@aloft/toolkit-shared';
import { colors } from '@aloft/toolkit-sui';

import { Bar, BarChart, CartesianGrid, Legend } from 'recharts';

import { selectCompSequence } from '../../../redux/features/selectors/orderSelectors.ts';
import { selectSiteEntities } from '../../../redux/features/selectors/propertyDataSelectors/siteSelectors.ts';
import { selectSiteValueRatios } from '../../../redux/features/selectors/selectSiteValueRatio.ts';
import { useAppSelector } from '../../../redux/hooks.ts';
import { GraphTitle } from '../components/GraphTitle.tsx';
import { GraphXAxis } from '../components/GraphXAxis.tsx';
import { GraphYAxis } from '../components/GraphYAxis.tsx';
import { ResponsiveGraphWrapper } from '../components/ResponsiveGraphWrapper.tsx';
import { SimpleGraphTooltip } from '../components/SimpleGraphTooltip.tsx';
import { GraphProps } from '../types.ts';
import { sortBySiteSize } from './utils.ts';

type ChartData = {
  propertyName: string;
  siteSize?: number;
  siteToValueRatio?: number;
};

export const SiteToValueRatioGraph = ({ disableAnimations = false, width, height }: GraphProps) => {
  const compSequence = useAppSelector(selectCompSequence);
  const siteEntities = useAppSelector(selectSiteEntities);
  const siteToValueRatios = useAppSelector(selectSiteValueRatios);

  const data: ChartData[] = sortBySiteSize(
    ['subject', ...compSequence].map((id, index) => {
      const area = siteEntities[id]?.area;
      return {
        propertyName: id === 'subject' ? 'Subject' : `Comp ${index}`,
        siteSize: area ? schAloftArea.parse(area).getUnitAmount('sqft') : undefined,
        siteToValueRatio: siteToValueRatios[id],
      };
    }),
  );

  return (
    <ResponsiveGraphWrapper disableAnimations={disableAnimations} width={width} height={height}>
      <BarChart
        data={data}
        width={width}
        height={height}
        margin={{ top: 48, left: 8, bottom: 8, right: 8 }}
        style={{ background: colors.graphBackground, borderRadius: 8 }}
      >
        <GraphTitle>Site to Value Ratio</GraphTitle>
        <CartesianGrid vertical={false} />
        <Legend />

        <SimpleGraphTooltip<ChartData>
          disableAnimations={disableAnimations}
          titleKey="propertyName"
          rows={[
            {
              label: 'Site to Value Ratio',
              key: 'siteToValueRatio',
              color: 'graphChart',
              valueFormatter: (v) => format.percent(v) ?? '-',
            },
          ]}
        />

        <GraphXAxis dataKey="propertyName" />
        <GraphYAxis
          label="Site to Value Ratio"
          tickFormatter={(v?: number) => format.percent(v)!}
        />

        <Bar
          name="Site to Value Ratio"
          dataKey="siteToValueRatio"
          fill={colors.graphChart}
          isAnimationActive={!disableAnimations}
        />
      </BarChart>
    </ResponsiveGraphWrapper>
  );
};
